// src/pages/admin/MenuManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner
} from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL } from '../../services/api';

const MenuManager = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  
  const [formData, setFormData] = useState({
    label: '',
    path: '/',
    icon: 'fas fa-link',
    menu_order: 0,
    is_active: true,
    is_external: false,
    parent_id: null
  });

  const [subFormData, setSubFormData] = useState({
    label: '',
    path: '/',
    icon: 'fas fa-link',
    menu_order: 0,
    is_active: true,
    is_external: false
  });

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const resetForm = () => {
    setFormData({
      label: '',
      path: '/',
      icon: 'fas fa-link',
      menu_order: 0,
      is_active: true,
      is_external: false,
      parent_id: null
    });
  };

  const resetSubForm = () => {
    setSubFormData({
      label: '',
      path: '/',
      icon: 'fas fa-link',
      menu_order: 0,
      is_active: true,
      is_external: false
    });
  };

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/menu.php?admin=true`);
      
      if (response.data.success) {
        // Process the data to ensure proper types for admin panel
        const processedItems = response.data.data.map(item => ({
          ...item,
          id: parseInt(item.id),
          menu_order: parseInt(item.menu_order),
          is_active: item.is_active === "1" || item.is_active === 1,
          is_external: item.is_external === "1" || item.is_external === 1,
          parent_id: item.parent_id ? parseInt(item.parent_id) : null,
          // Ensure subMenu exists and process it too
          subMenu: (item.subMenu || []).map(sub => ({
            ...sub,
            id: parseInt(sub.id),
            menu_order: parseInt(sub.menu_order),
            is_active: sub.is_active === "1" || sub.is_active === 1,
            is_external: sub.is_external === "1" || sub.is_external === 1,
            parent_id: parseInt(sub.parent_id)
          })).sort((a, b) => a.menu_order - b.menu_order) // Sort submenu items
        })).sort((a, b) => {
          // Sort main menu items by order, but keep them separate from submenu ordering
          if (!a.parent_id && !b.parent_id) {
            return a.menu_order - b.menu_order;
          }
          return 0;
        });
        
        setMenuItems(processedItems);
      } else {
        setError('Failed to load menu items');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      const dataToSend = {
        label: formData.label,
        path: formData.path,
        icon: formData.icon,
        menu_order: formData.menu_order,
        is_active: formData.is_active ? 1 : 0,
        is_external: formData.is_external ? 1 : 0,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null
      };
      
      if (editingItem) {
        response = await axios.put(
          `${API_BASE_URL}/menu.php?id=${editingItem.id}`,
          dataToSend
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/menu.php`, dataToSend);
      }
      
      if (response.data.success) {
        setSuccess(editingItem ? 'Menu item updated!' : 'Menu item added!');
        setShowModal(false);
        resetForm();
        setEditingItem(null);
        fetchMenuItems();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed');
      }
    } catch (err) {
      setError('Error saving menu item: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedParent) {
        setError('No parent selected');
        return;
      }
      
      const dataToSend = {
        label: subFormData.label,
        path: subFormData.path,
        icon: subFormData.icon,
        menu_order: subFormData.menu_order,
        is_active: subFormData.is_active ? 1 : 0,
        is_external: subFormData.is_external ? 1 : 0,
        parent_id: selectedParent.id
      };
      
      const response = await axios.post(`${API_BASE_URL}/menu.php`, dataToSend);
      
      if (response.data.success) {
        setSuccess('Submenu item added!');
        setShowSubModal(false);
        resetSubForm();
        setSelectedParent(null);
        fetchMenuItems();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to add submenu item');
      }
    } catch (err) {
      setError('Error adding submenu item');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/menu.php?id=${id}`);
        
        if (response.data.success) {
          setSuccess('Menu item deleted!');
          fetchMenuItems();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete menu item');
        }
      } catch (err) {
        setError('Error deleting menu item');
        console.error(err);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      label: item.label,
      path: item.path,
      icon: item.icon || 'fas fa-link',
      menu_order: parseInt(item.menu_order),
      is_active: item.is_active === true || item.is_active === "1" || item.is_active === 1,
      is_external: item.is_external === true || item.is_external === "1" || item.is_external === 1,
      parent_id: item.parent_id
    });
    setShowModal(true);
  };

  const handleAddSubmenu = (parentItem) => {
    setSelectedParent(parentItem);
    // Calculate next order number for submenu within this parent
    const subMenuCount = parentItem.subMenu ? parentItem.subMenu.length : 0;
    
    setSubFormData({
      label: '',
      path: '/',
      icon: 'fas fa-link',
      menu_order: subMenuCount + 1,
      is_active: true,
      is_external: false
    });
    setShowSubModal(true);
  };

  const handleToggleActive = async (item) => {
    try {
      // Determine current active status (handle both string and boolean)
      const currentActive = item.is_active === true || item.is_active === "1" || item.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        label: item.label,
        path: item.path,
        icon: item.icon || '',
        menu_order: parseInt(item.menu_order),
        is_active: newActiveStatus ? 1 : 0,
        is_external: (item.is_external === true || item.is_external === "1" || item.is_external === 1) ? 1 : 0,
        parent_id: item.parent_id || null
      };
      
      const response = await axios.put(
        `${API_BASE_URL}/menu.php?id=${item.id}`,
        updatedData
      );
      
      if (response.data.success) {
        fetchMenuItems();
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  // Reorder MAIN MENU items (only items with parent_id = null)
  const handleReorderMainMenu = async (direction, item) => {
    try {
      // Get all MAIN menu items (parent_id = null)
      const mainMenuItems = menuItems.filter(menuItem => !menuItem.parent_id);
      
      // Sort by current order
      const sortedMainItems = [...mainMenuItems].sort((a, b) => parseInt(a.menu_order) - parseInt(b.menu_order));
      const currentIndex = sortedMainItems.findIndex(sibling => sibling.id === item.id);
      
      if (direction === 'up' && currentIndex > 0) {
        // Swap with previous main menu item
        const prevItem = sortedMainItems[currentIndex - 1];
        
        // Update current item with previous item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${item.id}`, {
          ...item,
          menu_order: prevItem.menu_order,
          is_active: (item.is_active === true || item.is_active === "1" || item.is_active === 1) ? 1 : 0,
          is_external: (item.is_external === true || item.is_external === "1" || item.is_external === 1) ? 1 : 0,
          parent_id: null
        });
        
        // Update previous item with current item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${prevItem.id}`, {
          ...prevItem,
          menu_order: item.menu_order,
          is_active: (prevItem.is_active === true || prevItem.is_active === "1" || prevItem.is_active === 1) ? 1 : 0,
          is_external: (prevItem.is_external === true || prevItem.is_external === "1" || prevItem.is_external === 1) ? 1 : 0,
          parent_id: null
        });
        
        setSuccess('Main menu order updated!');
        setTimeout(() => setSuccess(''), 3000);
        
      } else if (direction === 'down' && currentIndex < sortedMainItems.length - 1) {
        // Swap with next main menu item
        const nextItem = sortedMainItems[currentIndex + 1];
        
        // Update current item with next item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${item.id}`, {
          ...item,
          menu_order: nextItem.menu_order,
          is_active: (item.is_active === true || item.is_active === "1" || item.is_active === 1) ? 1 : 0,
          is_external: (item.is_external === true || item.is_external === "1" || item.is_external === 1) ? 1 : 0,
          parent_id: null
        });
        
        // Update next item with current item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${nextItem.id}`, {
          ...nextItem,
          menu_order: item.menu_order,
          is_active: (nextItem.is_active === true || nextItem.is_active === "1" || nextItem.is_active === 1) ? 1 : 0,
          is_external: (nextItem.is_external === true || nextItem.is_external === "1" || nextItem.is_external === 1) ? 1 : 0,
          parent_id: null
        });
        
        setSuccess('Main menu order updated!');
        setTimeout(() => setSuccess(''), 3000);
      }
      
      fetchMenuItems();
      
    } catch (err) {
      console.error('Error reordering main menu:', err);
      setError('Failed to reorder main menu: ' + (err.response?.data?.message || err.message));
    }
  };

  // Reorder SUBMENU items within their parent
  const handleReorderSubmenu = async (direction, subItem, parentId) => {
    try {
      // Find the parent item
      const parentItem = menuItems.find(item => item.id === parentId);
      if (!parentItem || !parentItem.subMenu) return;
      
      // Get submenu items for this parent
      const subMenuItems = [...parentItem.subMenu].sort((a, b) => parseInt(a.menu_order) - parseInt(b.menu_order));
      const currentIndex = subMenuItems.findIndex(item => item.id === subItem.id);
      
      if (direction === 'up' && currentIndex > 0) {
        // Swap with previous submenu item
        const prevItem = subMenuItems[currentIndex - 1];
        
        // Update current subitem with previous item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${subItem.id}`, {
          ...subItem,
          menu_order: prevItem.menu_order,
          is_active: (subItem.is_active === true || subItem.is_active === "1" || subItem.is_active === 1) ? 1 : 0,
          is_external: (subItem.is_external === true || subItem.is_external === "1" || subItem.is_external === 1) ? 1 : 0,
          parent_id: parentId
        });
        
        // Update previous subitem with current item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${prevItem.id}`, {
          ...prevItem,
          menu_order: subItem.menu_order,
          is_active: (prevItem.is_active === true || prevItem.is_active === "1" || prevItem.is_active === 1) ? 1 : 0,
          is_external: (prevItem.is_external === true || prevItem.is_external === "1" || prevItem.is_external === 1) ? 1 : 0,
          parent_id: parentId
        });
        
        setSuccess('Submenu order updated!');
        setTimeout(() => setSuccess(''), 3000);
        
      } else if (direction === 'down' && currentIndex < subMenuItems.length - 1) {
        // Swap with next submenu item
        const nextItem = subMenuItems[currentIndex + 1];
        
        // Update current subitem with next item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${subItem.id}`, {
          ...subItem,
          menu_order: nextItem.menu_order,
          is_active: (subItem.is_active === true || subItem.is_active === "1" || subItem.is_active === 1) ? 1 : 0,
          is_external: (subItem.is_external === true || subItem.is_external === "1" || subItem.is_external === 1) ? 1 : 0,
          parent_id: parentId
        });
        
        // Update next subitem with current item's order
        await axios.put(`${API_BASE_URL}/menu.php?id=${nextItem.id}`, {
          ...nextItem,
          menu_order: subItem.menu_order,
          is_active: (nextItem.is_active === true || nextItem.is_active === "1" || nextItem.is_active === 1) ? 1 : 0,
          is_external: (nextItem.is_external === true || nextItem.is_external === "1" || nextItem.is_external === 1) ? 1 : 0,
          parent_id: parentId
        });
        
        setSuccess('Submenu order updated!');
        setTimeout(() => setSuccess(''), 3000);
      }
      
      fetchMenuItems();
      
    } catch (err) {
      console.error('Error reordering submenu:', err);
      setError('Failed to reorder submenu: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    resetForm();
  };

  const handleCloseSubModal = () => {
    setShowSubModal(false);
    setSelectedParent(null);
    resetSubForm();
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading menu items...</p>
      </Container>
    );
  }

  // Filter main menu items (without parent) and sort by order
  const mainMenuItems = menuItems
    .filter(item => !item.parent_id)
    .sort((a, b) => parseInt(a.menu_order) - parseInt(b.menu_order));

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Menu Manager</h2>
          <p className="text-muted">Manage your website navigation menu</p>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => {
            // Calculate next order number for new main menu item
            const nextOrder = mainMenuItems.length > 0 
              ? Math.max(...mainMenuItems.map(item => parseInt(item.menu_order))) + 1 
              : 1;
            setFormData(prev => ({ 
              ...prev, 
              label: '',
              path: '/',
              icon: 'fas fa-link',
              menu_order: nextOrder,
              is_active: true,
              is_external: false,
              parent_id: null
            }));
            setShowModal(true);
          }}>
            <i className="fas fa-plus me-2"></i>Add Menu Item
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th width="50">Order</th>
                <th>Label</th>
                <th>Path</th>
                <th>Type</th>
                <th>Status</th>
                <th width="200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mainMenuItems.map((item, mainIndex) => {
                // Get submenu items from the item.subMenu property
                const subMenuItems = item.subMenu || [];
                
                // Calculate main menu serial number (starts from 1)
                const mainSerialNumber = mainIndex + 1;
                
                return (
                  <React.Fragment key={item.id}>
                    {/* Main menu item */}
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorderMainMenu('up', item)}
                            disabled={parseInt(item.menu_order) <= 1}
                          >
                            <i className="fas fa-arrow-up"></i>
                          </Button>
                          <span className="mx-2">{mainSerialNumber}</span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorderMainMenu('down', item)}
                            disabled={parseInt(item.menu_order) >= mainMenuItems.length}
                          >
                            <i className="fas fa-arrow-down"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        {item.icon && <i className={`${item.icon} me-2`} />}
                        <strong>{item.label}</strong>
                      </td>
                      <td>
                        <small className="text-muted">{item.path}</small>
                        {item.is_external && <Badge bg="warning" className="ms-2">External</Badge>}
                      </td>
                      <td>
                        {subMenuItems.length > 0 ? (
                          <Badge bg="primary">Parent ({subMenuItems.length})</Badge>
                        ) : (
                          <Badge bg="secondary">Single</Badge>
                        )}
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={item.is_active === true || item.is_active === "1" || item.is_active === 1}
                          onChange={() => handleToggleActive(item)}
                          label={(item.is_active === true || item.is_active === "1" || item.is_active === 1) ? 'Active' : 'Inactive'}
                          key={`active-${item.id}-${item.is_active}`}
                        />
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(item)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="me-2"
                          onClick={() => handleAddSubmenu(item)}
                        >
                          <i className="fas fa-plus-circle"></i> Sub
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                    
                    {/* Submenu items */}
                    {subMenuItems.map((subItem, subIndex) => {
                      // Calculate submenu serial number (starts from 1 for each parent)
                      const subSerialNumber = subIndex + 1;
                      
                      return (
                        <tr key={subItem.id} className="table-light">
                          <td className="ps-5">
                            <div className="d-flex align-items-center">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => handleReorderSubmenu('up', subItem, item.id)}
                                disabled={parseInt(subItem.menu_order) <= 1}
                              >
                                <i className="fas fa-arrow-up"></i>
                              </Button>
                              <span className="mx-2">{mainSerialNumber}.{subSerialNumber}</span>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => handleReorderSubmenu('down', subItem, item.id)}
                                disabled={parseInt(subItem.menu_order) >= subMenuItems.length}
                              >
                                <i className="fas fa-arrow-down"></i>
                              </Button>
                            </div>
                          </td>
                          <td className="ps-5">
                            <i className="fas fa-level-down-alt me-2 text-muted"></i>
                            {subItem.icon && <i className={`${subItem.icon} me-2`} />}
                            {subItem.label}
                          </td>
                          <td className="ps-5">
                            {subItem.path}
                            {subItem.is_external && <Badge bg="warning" className="ms-2">External</Badge>}
                          </td>
                          <td>
                            <Badge bg="info">Submenu</Badge>
                          </td>
                          <td>
                            <Form.Check
                              type="switch"
                              checked={subItem.is_active === true || subItem.is_active === "1" || subItem.is_active === 1}
                              onChange={() => handleToggleActive(subItem)}
                              label={(subItem.is_active === true || subItem.is_active === "1" || subItem.is_active === 1) ? 'Active' : 'Inactive'}
                              key={`active-sub-${subItem.id}-${subItem.is_active}`}
                            />
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-2"
                              onClick={() => handleEdit(subItem)}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(subItem.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
          
          {mainMenuItems.length === 0 && (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x mb-3"></i>
              <p>No menu items found. Add your first menu item!</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Menu Item Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Label *</Form.Label>
              <Form.Control
                type="text"
                name="label"
                value={formData.label}
                onChange={handleInputChange}
                required
                placeholder="e.g., Home, About"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Path *</Form.Label>
              <Form.Control
                type="text"
                name="path"
                value={formData.path}
                onChange={handleInputChange}
                required
                placeholder="/about"
              />
              <Form.Text className="text-muted">
                Use # for dropdown parents without link
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Icon Class</Form.Label>
              <Form.Control
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                placeholder="fas fa-home"
              />
              <Form.Text className="text-muted">
                FontAwesome classes (e.g., fas fa-home)
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Order</Form.Label>
              <Form.Control
                type="number"
                name="menu_order"
                value={formData.menu_order}
                onChange={handleInputChange}
                min="1"
              />
              <Form.Text className="text-muted">
                Lower numbers appear first (among items with same parent)
              </Form.Text>
            </Form.Group>
            
            <Row>
              <Col>
                <Form.Check
                  type="checkbox"
                  name="is_active"
                  label="Active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="mb-3"
                />
              </Col>
              <Col>
                <Form.Check
                  type="checkbox"
                  name="is_external"
                  label="External Link"
                  checked={formData.is_external}
                  onChange={handleInputChange}
                  className="mb-3"
                />
              </Col>
            </Row>
            
            {!editingItem && (
              <Form.Group className="mb-3">
                <Form.Label>Parent Menu (Optional)</Form.Label>
                <Form.Select
                  name="parent_id"
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({...formData, parent_id: e.target.value || null})}
                >
                  <option value="">-- No Parent (Main Menu) --</option>
                  {mainMenuItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select a parent menu to create a submenu item
                </Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingItem ? 'Update' : 'Add'} Item
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Submenu Modal */}
      <Modal show={showSubModal} onHide={handleCloseSubModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            Add Submenu to: {selectedParent?.label}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Label *</Form.Label>
              <Form.Control
                type="text"
                name="label"
                value={subFormData.label}
                onChange={handleSubInputChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Path *</Form.Label>
              <Form.Control
                type="text"
                name="path"
                value={subFormData.path}
                onChange={handleSubInputChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Icon Class</Form.Label>
              <Form.Control
                type="text"
                name="icon"
                value={subFormData.icon}
                onChange={handleSubInputChange}
                placeholder="fas fa-cog"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Order</Form.Label>
              <Form.Control
                type="number"
                name="menu_order"
                value={subFormData.menu_order}
                onChange={handleSubInputChange}
                min="1"
              />
              <Form.Text className="text-muted">
                Lower numbers appear first (within this submenu)
              </Form.Text>
            </Form.Group>
            
            <Row>
              <Col>
                <Form.Check
                  type="checkbox"
                  name="is_active"
                  label="Active"
                  checked={subFormData.is_active}
                  onChange={handleSubInputChange}
                />
              </Col>
              <Col>
                <Form.Check
                  type="checkbox"
                  name="is_external"
                  label="External Link"
                  checked={subFormData.is_external}
                  onChange={handleSubInputChange}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseSubModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Submenu
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default MenuManager;